import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

interface SettingRow {
  id: string;
  value: string;
  order?: number | null;
  active?: boolean;
  groupName?: string | null;
  shortKey?: string | null;
}

export type ReferenceTable =
  | 'roles'
  | 'statuses'
  | 'cert-types'
  | 'supplier-categories'
  | 'doc-types'
  | 'durations';

/**
 * Read a Settings-managed reference list directly from the DB. Use this anywhere
 * a dropdown / checkbox group needs values that the user can edit in
 * Settings → {Worker Roles, Statuses, Cert Types, Supplier Categories, Doc Types,
 * Deployment Durations}. The query key matches the Settings page so a single
 * fetch is shared across the app.
 *
 * `fallback` is used only when the DB list is empty (e.g. first-boot before the
 * tables are seeded). Once the user adds entries, the DB list takes precedence.
 */
export function useReferenceList(
  table: ReferenceTable,
  fallback: readonly string[] = [],
): { values: string[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', table],
    queryFn: () => api.get<SettingRow[]>(`/settings/${table}`).then((r) => r.data).catch(() => [] as SettingRow[]),
  });
  const rows = data ?? [];
  const active = rows.filter((r) => r.active !== false).map((r) => r.value);
  const values = active.length > 0 ? active : [...fallback];
  return { values, isLoading };
}

/**
 * Same as useReferenceList but returns the raw rows so callers can use
 * `groupName` / `shortKey` / `order` (e.g. cert-types' group → matrix column).
 */
export function useReferenceRows(
  table: ReferenceTable,
): { rows: SettingRow[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', table],
    queryFn: () => api.get<SettingRow[]>(`/settings/${table}`).then((r) => r.data).catch(() => [] as SettingRow[]),
  });
  return { rows: data ?? [], isLoading };
}
